import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getVariableSlotValue,
  isArrayVariable,
  normalizeVariableName,
  variableNameKey,
} from '../../../utils/variableScope';
import { VARIABLE_TYPE_CHOICES } from '../../../utils/variableTypeChange';
import { VariableTypeChoices } from '../components/VariableTypeChoices';

/**
 * Inline search + pick list for a linked variable row (empty / missing bind).
 */
const LinkedVariableRowPicker = ({
  variables = [],
  missingName = '',
  onPick,
  onCreate,
}) => {
  const inputRef = useRef(null);
  const ignoreEnterUntilRef = useRef(0);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [creatingName, setCreatingName] = useState('');

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    // The slash menu's Enter must not immediately pick the first result.
    ignoreEnterUntilRef.current = Date.now() + 120;
    input.focus({ preventScroll: true });
    // Slash Enter + the trailing text block can steal focus on the same tick.
    const retry = window.setTimeout(() => {
      if (document.activeElement !== input) input.focus({ preventScroll: true });
    }, 40);
    return () => window.clearTimeout(retry);
  }, []);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    const list = variables || [];
    if (!q) return list;
    return list.filter((v) => {
      const name = String(v.name || '').toLowerCase();
      const desc = String(v.description || '').toLowerCase();
      const value = String(getVariableSlotValue(v) || '').toLowerCase();
      return name.includes(q) || desc.includes(q) || value.includes(q);
    });
  }, [variables, q]);

  const trimmedName = normalizeVariableName(query).replace(/\s+/g, '_');
  const nameTaken =
    !!trimmedName &&
    variables.some((v) => variableNameKey(v.name) === variableNameKey(trimmedName));
  const canCreate = !!onCreate && !!trimmedName && !nameTaken;
  const createIndex = results.length;
  const optionCount = creatingName
    ? VARIABLE_TYPE_CHOICES.length
    : results.length + (canCreate ? 1 : 0);

  const beginCreate = () => {
    if (!canCreate) return;
    setCreatingName(trimmedName);
    setSelectedIndex(0);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [q]);

  const pick = (variable) => {
    if (!variable) return;
    onPick?.(variable);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!optionCount) return;
      setSelectedIndex((prev) => (prev + 1) % optionCount);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!optionCount) return;
      setSelectedIndex((prev) => (prev - 1 + optionCount) % optionCount);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (Date.now() < ignoreEnterUntilRef.current) return;
      if (creatingName) {
        const choice = VARIABLE_TYPE_CHOICES[selectedIndex];
        if (choice) onCreate(creatingName, choice.type);
        return;
      }
      if (results[selectedIndex]) {
        pick(results[selectedIndex]);
        return;
      }
      if (canCreate && selectedIndex === createIndex) beginCreate();
    }
    if (e.key === 'Escape' && creatingName) {
      e.preventDefault();
      setCreatingName('');
    }
  };

  const previewOf = (v) => {
    if (isArrayVariable(v)) {
      const n = (Array.isArray(v.values) ? v.values : []).filter(
        (slot) => String(slot ?? '').trim() !== ''
      ).length;
      return n ? `${n} value${n === 1 ? '' : 's'}` : 'no values yet';
    }
    const value = String(v.value ?? '').trim();
    return value || 'no value yet';
  };

  return (
    <div className="linked-variable-row-picker">
      <div className="linked-variable-row-picker__title">
        {missingName
          ? `“${missingName}” was deleted — pick another`
          : 'Select a variable'}
      </div>
      <input
        ref={inputRef}
        className="linked-variable-row-picker__search"
        value={query}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        lang="zxx"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search variables…"
        aria-label="Search variables"
      />
      <div className="linked-variable-row-picker__list" role="listbox">
        {creatingName ? (
          <VariableTypeChoices
            name={creatingName}
            selectedIndex={selectedIndex}
            onChoose={(type) => onCreate(creatingName, type)}
            onHover={setSelectedIndex}
            itemClassName="linked-variable-row-picker__item"
            selectedClassName="is-selected"
          />
        ) : results.map((v, index) => (
          <button
            key={`${v.scope || 'doc'}-${v.sourceId || 'local'}-${v.id || v.name}`}
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            className={`linked-variable-row-picker__item${
              index === selectedIndex ? ' is-selected' : ''
            }`}
            onClick={() => pick(v)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span
              className="linked-variable-row-picker__dot"
              style={v.color ? { background: v.color } : undefined}
            />
            <span className="linked-variable-row-picker__name">{v.name || 'untitled'}</span>
            <span className="linked-variable-row-picker__meta">
              {previewOf(v)}
              {v.sourceName && v.scope && v.scope !== 'doc' ? ` — ${v.sourceName}` : ''}
            </span>
          </button>
        ))}
        {!creatingName && results.length === 0 && !canCreate && (
          <div className="linked-variable-row-picker__empty">
            {variables.length === 0
              ? 'No variables yet — type a name to create one'
              : 'No matches'}
          </div>
        )}
        {!creatingName && canCreate && (
          <button
            type="button"
            className={`linked-variable-row-picker__item linked-variable-row-picker__item--create${
              selectedIndex === createIndex ? ' is-selected' : ''
            }`}
            onClick={() => beginCreate()}
            onMouseEnter={() => setSelectedIndex(createIndex)}
          >
            <span className="linked-variable-row-picker__create-mark">+</span>
            <span className="linked-variable-row-picker__name">
              Create {trimmedName}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default LinkedVariableRowPicker;
